import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Data, Params, RouterStateSnapshot, UrlSegment } from '@angular/router';
import { AppRouteData } from '@app/shared/utils/route-utils';
import { BaseRouterStoreState, RouterStateSerializer } from '@ngrx/router-store';

export interface ParamNode {
  depth: number;
  value: any;
}

export interface ParamNodes {
  [key: string]: ParamNode[];
}

export interface RouterStateUrl extends BaseRouterStoreState {
  params: Params;
  queryParams: Params;
  flatParams: Params;
  flatParamNodes: ParamNodes;
  routeParams: Params[];
  routeData: AppRouteData[];
  urlSegments: UrlSegment[][];
}

@Injectable()
export class CustomRouterStateSerializer implements RouterStateSerializer<RouterStateUrl> {
  serialize(routerState: RouterStateSnapshot): RouterStateUrl {
    let route = routerState.root;

    const flatParamNodes = this.collectParamNodes(route);
    const flatParams = this.flattenParams(route);
    const routeParams = this.collectParams(route);
    const routeData = this.collectData(route);
    const urlSegments = this.collectUrlSegments(route);

    while (route.firstChild) {
      route = route.firstChild;
    }

    const {
      url,
      root: { queryParams },
    } = routerState;
    const { params } = route;

    return {
      url,
      params,
      queryParams,
      flatParamNodes,
      flatParams,
      routeParams,
      routeData,
      urlSegments,
    };
  }

  /**
   * Returns Params with a single value for each key.  Route params in
   * child routes override those with the same key in parent routes.
   */
  flattenParams(snapshot: ActivatedRouteSnapshot, params: Params = {}): Params {
    Object.getOwnPropertyNames(snapshot.params).forEach((key) => {
      params[key] = snapshot.params[key];
    });

    if (snapshot.firstChild) {
      return this.flattenParams(snapshot.firstChild, params);
    } else {
      return params;
    }
  }

  collectParams(
    snapshot: ActivatedRouteSnapshot,
    params: Params[] = [],
    depth = 0,
  ): Params[] {
    if (depth === 0 && snapshot !== snapshot.root) {
      return this.collectParams(snapshot.root);
    }

    params.push(snapshot.params);

    if (snapshot.firstChild) {
      return this.collectParams(snapshot.firstChild, params, depth + 1);
    }

    return params;
  }

  collectData(snapshot: ActivatedRouteSnapshot, data: Data[] = [], depth = 0): Data[] {
    if (depth === 0 && snapshot !== snapshot.root) {
      return this.collectData(snapshot.root);
    }

    data.push(snapshot.data);

    if (snapshot.firstChild) {
      return this.collectData(snapshot.firstChild, data, depth + 1);
    }

    return data;
  }

  collectUrlSegments(
    snapshot: ActivatedRouteSnapshot,
    urlSegments: UrlSegment[][] = [],
    depth = 0,
  ): UrlSegment[][] {
    if (depth === 0 && snapshot !== snapshot.root) {
      return this.collectUrlSegments(snapshot.root);
    }

    urlSegments.push(snapshot.url);

    if (snapshot.firstChild) {
      return this.collectUrlSegments(snapshot.firstChild, urlSegments, depth + 1);
    }

    return urlSegments;
  }

  collectParamNodes(
    snapshot: ActivatedRouteSnapshot,
    paramNodes: ParamNodes = {},
    depth = 0,
  ): ParamNodes {
    if (depth === 0 && snapshot !== snapshot.root) {
      return this.collectParamNodes(snapshot.root);
    }

    Object.getOwnPropertyNames(snapshot.params).forEach((key) => {
      if (paramNodes[key] == null) {
        paramNodes[key] = [];
      }

      paramNodes[key].push({
        depth,
        value: snapshot.params[key],
      });
    });

    if (snapshot.firstChild) {
      return this.collectParamNodes(snapshot.firstChild, paramNodes, depth + 1);
    } else {
      return paramNodes;
    }
  }
}

export const selectParamNodes = (state: RouterStateUrl) => state.flatParamNodes;
export const selectParamNodesForKey = (key: string) => (paramNodes: ParamNodes) => paramNodes[key];
export const selectParamValueForKeyAndDepth = (key: string, depth: number) => (
  paramNodes: ParamNodes,
) => {
  const paramNode = paramNodes[key] && paramNodes[key].find(node => node.depth === depth);
  if (paramNode) {
    return paramNode.value;
  }
};

export const selectRouteParams = (state: RouterStateUrl) => state.routeParams;
export const selectParamsForDepth = (depth: number) => (params: Params[]) => params[depth];

export const selectRouteData = (state: RouterStateUrl) => state.routeData;
export const selectDataForDepth = (depth: number) => (data: Data[]) => data[depth];
